/**
 * Hoekradius voor producten met afgeronde hoeken.
 *
 * Twee soorten "afgeronde hoeken" komen voor:
 *
 * 1. De vormpreset `custom_shape_simple|shape:rounded_corners` (zie
 *    src/lib/products/custom-shape-presets.ts). De leverancier snijdt dan
 *    langs een contour die in het drukbestand moet staan. De klant kiest de
 *    straal zelf (standaard 10 mm); die staat in de configuratie als
 *    `corner_radius_mm` en gaat nooit als optie naar de leverancier (zie
 *    normalizePrintComOptionsForSku).
 * 2. Een vaste keuze `rounded_corners` (bijvoorbeeld `5_mm_radius`). De
 *    leverancier rondt dan zelf af met een vaste straal; daar is geen vrije
 *    invoer en geen snijlijn in het bestand nodig.
 */

export const CORNER_RADIUS_OPTION_ID = 'corner_radius_mm';
export const DEFAULT_CORNER_RADIUS_MM = 10;
export const MIN_CORNER_RADIUS_MM = 2;
/** Bovengrens zonder bekend formaat: voorkomt onzinwaarden in de configuratie. */
export const ABSOLUTE_MAX_CORNER_RADIUS_MM = 500;

const ROUNDED_PRESET_PATTERN = /\|shape:rounded[_-]corners\b/i;

type Options = Record<string, unknown> | null | undefined;
type SizeMm = { widthMm?: number | null; heightMm?: number | null } | null | undefined;

/** True voor de vormpreset "Afgeronde hoeken" met snijcontour uit het bestand. */
export function isRoundedCornersShapePreset(customShape: unknown): boolean {
  return ROUNDED_PRESET_PATTERN.test(String(customShape ?? ''));
}

/** Of deze configuratie een vrij te kiezen hoekradius met snijlijn heeft. */
export function usesCustomCornerRadius(options: Options): boolean {
  return isRoundedCornersShapePreset(options?.custom_shape);
}

function parseNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string' || !value.trim()) return NaN;
  return Number(value.trim().replace(',', '.'));
}

/** Grootste toegestane straal: de helft van de korte zijde van het eindformaat. */
export function maxCornerRadiusMm(size?: SizeMm): number {
  const width = Number(size?.widthMm);
  const height = Number(size?.heightMm);
  if (!(width > 0) || !(height > 0)) return ABSOLUTE_MAX_CORNER_RADIUS_MM;
  return Math.max(MIN_CORNER_RADIUS_MM, Math.min(ABSOLUTE_MAX_CORNER_RADIUS_MM, Math.floor(Math.min(width, height) / 2)));
}

export type CornerRadiusValidation =
  | { ok: true; value: number }
  | { ok: false; reason: 'not_a_number' | 'too_small' | 'too_large'; min: number; max: number };

/** Controleert een ingevoerde straal tegen 2 mm .. halve korte zijde. */
export function validateCornerRadiusMm(value: unknown, size?: SizeMm): CornerRadiusValidation {
  const min = MIN_CORNER_RADIUS_MM;
  const max = maxCornerRadiusMm(size);
  const number = parseNumber(value);
  if (!Number.isFinite(number)) return { ok: false, reason: 'not_a_number', min, max };
  const rounded = Math.round(number * 10) / 10;
  if (rounded < min) return { ok: false, reason: 'too_small', min, max };
  if (rounded > max) return { ok: false, reason: 'too_large', min, max };
  return { ok: true, value: rounded };
}

/** Een geldige straal: de opgegeven waarde begrensd, of de standaard (10 mm). */
export function normalizeCornerRadiusMm(value: unknown, size?: SizeMm): number {
  const min = MIN_CORNER_RADIUS_MM;
  const max = maxCornerRadiusMm(size);
  const number = parseNumber(value);
  const base = Number.isFinite(number) ? Math.round(number * 10) / 10 : DEFAULT_CORNER_RADIUS_MM;
  return Math.max(min, Math.min(max, base));
}

/** Straal uit een vaste leverancierskeuze zoals `3,5_mm_radius` of `10 mm`. */
export function parseFixedCornerRadiusMm(value: unknown): number {
  const match = String(value ?? '').match(/(\d+(?:[.,]\d+)?)\s*_?\s*mm/i);
  return match ? Number(match[1].replace(',', '.')) : 0;
}

/**
 * De straal die de editor als hulplijn toont en (bij de vormpreset) als
 * snijlijn in het bestand zet. 0 = rechte hoeken.
 */
export function resolveCornerRadiusMm(options: Options, size?: SizeMm): number {
  if (usesCustomCornerRadius(options)) return normalizeCornerRadiusMm(options?.[CORNER_RADIUS_OPTION_ID], size);
  return parseFixedCornerRadiusMm(options?.rounded_corners);
}

/**
 * Configuratie na een vormkeuze: bij de preset een geldige straal (de
 * bestaande of 10 mm), anders geen `corner_radius_mm` meer.
 */
export function withCornerRadiusForShape<T extends Record<string, unknown>>(options: T, size?: SizeMm): T {
  const next = { ...options } as Record<string, unknown>;
  if (usesCustomCornerRadius(next)) next[CORNER_RADIUS_OPTION_ID] = normalizeCornerRadiusMm(next[CORNER_RADIUS_OPTION_ID], size);
  else delete next[CORNER_RADIUS_OPTION_ID];
  return next as T;
}

const LABELS: Record<string, { label: string; help: string; redesign: string; errorRange: (min: number, max: number) => string }> = {
  nl: {
    label: 'Hoekradius (mm)',
    help: 'De snijlijn met deze hoekradius komt automatisch in je drukbestand.',
    redesign: 'Je ontwerp is opgeslagen met een andere hoekradius. Open het ontwerp opnieuw en sla het op, dan komt de snijlijn met de nieuwe radius in je drukbestand.',
    errorRange: (min, max) => `Kies een hoekradius tussen ${min} en ${max} mm.`,
  },
  en: {
    label: 'Corner radius (mm)',
    help: 'The cut line with this corner radius is added to your print file automatically.',
    redesign: 'Your design was saved with a different corner radius. Open the design again and save it, so the cut line with the new radius goes into your print file.',
    errorRange: (min, max) => `Choose a corner radius between ${min} and ${max} mm.`,
  },
  de: {
    label: 'Eckenradius (mm)',
    help: 'Die Schneidelinie mit diesem Eckenradius wird automatisch in Ihre Druckdatei eingefügt.',
    redesign: 'Ihr Design wurde mit einem anderen Eckenradius gespeichert. Öffnen Sie das Design erneut und speichern Sie es, damit die Schneidelinie mit dem neuen Radius in Ihre Druckdatei kommt.',
    errorRange: (min, max) => `Wählen Sie einen Eckenradius zwischen ${min} und ${max} mm.`,
  },
  fr: {
    label: 'Rayon des coins (mm)',
    help: 'La ligne de découpe avec ce rayon est ajoutée automatiquement à votre fichier d’impression.',
    redesign: 'Votre design a été enregistré avec un autre rayon. Rouvrez le design et enregistrez-le pour que la ligne de découpe avec le nouveau rayon soit dans votre fichier d’impression.',
    errorRange: (min, max) => `Choisissez un rayon entre ${min} et ${max} mm.`,
  },
};

export function cornerRadiusCopy(locale?: string | null) {
  return LABELS[String(locale || 'nl').slice(0, 2)] || LABELS.en;
}

/** Weergaverij voor winkelwagen, orderoverzicht en admin. */
export function cornerRadiusDisplayRow(options: Options, locale?: string | null) {
  if (!usesCustomCornerRadius(options)) return null;
  const value = normalizeCornerRadiusMm(options?.[CORNER_RADIUS_OPTION_ID]);
  return {
    key: CORNER_RADIUS_OPTION_ID,
    label: cornerRadiusCopy(locale).label.replace(/\s*\(mm\)$/, ''),
    value: `${String(value).replace('.', locale === 'en' ? '.' : ',')} mm`,
  };
}

/**
 * True when an editor design (printenMeta) was saved with a different corner
 * radius than the configuration now orders for the rounded corners preset.
 * Uploads without editor data are not affected: the cut is added at
 * production prepare with the ordered radius.
 */
export function designCornerRadiusMismatch(designData: unknown, options: Options, size?: SizeMm): boolean {
  if (!usesCustomCornerRadius(options)) return false;
  const meta = (designData as any)?.json?.printenMeta;
  if (!meta || typeof meta !== 'object') return false;
  const drawn = Number(meta.roundedCornerRadiusMm) || 0;
  return Math.abs(drawn - normalizeCornerRadiusMm(options?.[CORNER_RADIUS_OPTION_ID], size)) > 0.05;
}
